
import React, { useState } from 'react';
import { Building2, Mail, Lock, User as UserIcon, ShieldCheck, Loader2, ArrowLeft, ChevronRight, Link } from 'lucide-react';
import { resendSignupConfirmation, signInWithPassword, signUpWithEmail } from '../services/authService';
import SupabaseConfigNotice from './SupabaseConfigNotice';

interface AuthPageProps {
  onAuthDone: () => void;
  onBack: () => void;
}

// NOTE: Always add data-testid to critical auth elements for testability.


const AuthPage: React.FC<AuthPageProps> = ({ onAuthDone, onBack }) => {
  const [authMode, setAuthMode] = useState<'login' | 'register'>('login');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  
  // Form State
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setIsLoading(true);

    try {
      const result = authMode === 'login'
        ? await signInWithPassword(email, password)
        : await signUpWithEmail(email, password, name);

      if (result.status === 'signed_in') {
        onAuthDone();
        return;
      }

      if (result.status === 'needs_email_verification') {
        setInfo('Email verification required. Click "Resend verification email" if you didn’t receive it.');
        onAuthDone(); // will route into EmailVerificationGate
        return;
      }

      setError(result.message);
    } finally {
      setIsLoading(false);
    }
  };


  const handleResend = async () => {
    setError('');
    setInfo('');
    setIsLoading(true);
    try {
      const result = await resendSignupConfirmation(email);
      if (result.status === 'error') {
        setError(result.message);
      } else {
        setInfo('Verification email re-sent. Please check your inbox (and spam).');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 font-sans text-slate-300 flex relative overflow-hidden">
        
        {/* Ambient Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-[-10%] right-[-5%] w-[600px] h-[600px] bg-emerald-600/10 rounded-full blur-[100px] opacity-50"></div>
            <div className="absolute bottom-[-10%] left-[-5%] w-[600px] h-[600px] bg-blue-600/10 rounded-full blur-[100px] opacity-50"></div>
        </div>

        {/* Left Side: Branding (Hidden on Mobile) */}
        <div className="hidden lg:flex flex-col justify-between w-1/2 p-12 relative z-10 border-r border-slate-800/50 bg-slate-950/50 backdrop-blur-sm">
            <div 
                onClick={onBack}
                className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors cursor-pointer w-fit"
            >
                <ArrowLeft className="w-4 h-4" /> Back to Landing
            </div>

            <div className="max-w-lg">
                <div className="w-16 h-16 bg-slate-900 rounded-2xl flex items-center justify-center mb-8 border border-slate-800 shadow-xl">
                    <Link className="w-8 h-8 text-emerald-400" />
                </div>
                <h1 className="text-5xl font-black text-white tracking-tight mb-6">
                    Investor <br/> 
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-500">
                        Access Terminal
                    </span>
                </h1>
                <p className="text-lg text-slate-400 leading-relaxed">
                    Securely manage your real estate portfolio with DC PropChain. Access tax simulations, market data, and AI insights in one unified dashboard.
                </p>
            </div>

            <div className="flex items-center gap-4 text-xs font-mono text-slate-500">
                <ShieldCheck className="w-4 h-4 text-emerald-500" />
                <span>256-BIT ENCRYPTION ACTIVE</span>
            </div>
        </div>

        {/* Right Side: Form */}
        <div className="flex-1 flex items-center justify-center p-6 relative z-10">
            <button 
                onClick={onBack}
                className="absolute top-6 left-6 lg:hidden flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
            >
                <ArrowLeft className="w-4 h-4" /> Back
            </button>

            <div className="w-full max-w-md">
                <div className="bg-slate-900/60 backdrop-blur-xl rounded-3xl border border-slate-800 p-8 shadow-2xl">
                    <div className="mb-8 text-center">
                         <h2 className="text-2xl font-bold text-white tracking-tight">
                            {authMode === 'login' ? 'System Login' : 'New Account'}
                        </h2>
                        <p className="text-slate-500 text-sm mt-2">
                            {authMode === 'login' ? 'Enter your credentials to access the dashboard.' : 'Join the platform for free today.'}
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-5">
                        {authMode === 'register' && (
                            <div className="animate-in slide-in-from-left-5 fade-in duration-300">
                                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1.5 ml-1">Full Identity</label>
                                <div className="relative group">
                                    <UserIcon className="w-5 h-5 absolute left-3 top-3.5 text-slate-500 group-focus-within:text-emerald-400 transition-colors" />
                                    <input 
                                        type="text" 
                                        required
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        placeholder="Full Name"
                                        className="w-full pl-10 pr-4 py-3 bg-slate-950 border border-slate-800 rounded-xl focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 outline-none transition-all text-white placeholder-slate-600" 
                                        data-testid="auth-register-name-input"
                                    />
                                </div>
                            </div>
                        )}
                        
                        <div>
                            <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1.5 ml-1">Email Address</label>
                            <div className="relative group">
                                <Mail className="w-5 h-5 absolute left-3 top-3.5 text-slate-500 group-focus-within:text-emerald-400 transition-colors" />
                                <input 
                                    type="email" 
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="investor@example.com"
                                    className="w-full pl-10 pr-4 py-3 bg-slate-950 border border-slate-800 rounded-xl focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 outline-none transition-all text-white placeholder-slate-600" 
                                    data-testid="auth-email-input"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1.5 ml-1">Security Key</label>
                            <div className="relative group">
                                <Lock className="w-5 h-5 absolute left-3 top-3.5 text-slate-500 group-focus-within:text-emerald-400 transition-colors" />
                                <input 
                                    type="password" 
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                    className="w-full pl-10 pr-4 py-3 bg-slate-950 border border-slate-800 rounded-xl focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 outline-none transition-all text-white placeholder-slate-600" 
                                    data-testid="auth-password-input"
                                />
                            </div>
                        </div>

                        {info && (
                            <div className="bg-emerald-500/10 text-emerald-300 text-sm p-4 rounded-xl border border-emerald-500/20 animate-in slide-in-from-top-2" data-testid="auth-info">
                                <div className="flex items-center gap-2">
                                  <ShieldCheck className="w-4 h-4" /> {info}
                                </div>
                                <div className="mt-3 flex gap-2">
                                  <button
                                    type="button"
                                    onClick={handleResend}
                                    className="px-3 py-2 text-xs font-bold bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg"
                                    data-testid="auth-resend-verification-button"
                                  >
                                    Resend verification email
                                  </button>
                                </div>
                            </div>
                        )}

                        {error && (
                            <div className="space-y-3" data-testid="auth-error">
                              <div className="bg-red-500/10 text-red-400 text-sm p-4 rounded-xl flex items-center gap-2 border border-red-500/20 animate-in slide-in-from-top-2">
                                  <ShieldCheck className="w-4 h-4" /> {error}
                              </div>
                              {(error.toLowerCase().includes('redirect') || error.toLowerCase().includes('400') || error.toLowerCase().includes('configuration')) && (
                                <div className="mt-2">
                                  {/** Helpful guidance when Supabase is misconfigured */}
                                  <SupabaseConfigNotice message="Your Supabase project likely has an invalid Redirect URL allowlist / Site URL. Fix that first, then retry signup." />
                                </div>
                              )}
                            </div>
                        )}

                        <button 
                            type="submit" 
                            disabled={isLoading}
                            className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-3.5 rounded-xl font-bold hover:from-emerald-500 hover:to-teal-500 transition-all flex justify-center items-center gap-2 shadow-[0_0_20px_rgba(16,185,129,0.2)] hover:shadow-[0_0_30px_rgba(16,185,129,0.4)] transform hover:-translate-y-0.5"
                            data-testid="auth-submit-button"
                        >
                            {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                                <>
                                    {authMode === 'login' ? 'Authenticate' : 'Initialize Account'}
                                    <ChevronRight className="w-4 h-4" />
                                </>
                            )}
                        </button>
                    </form>

                    <div className="mt-8 pt-6 border-t border-slate-800 text-center text-sm">
                        {authMode === 'login' ? (
                            <p className="text-slate-500">
                                First time here? {' '}
                                <button onClick={() => setAuthMode('register')} className="text-emerald-400 font-bold hover:text-emerald-300 transition-colors" data-testid="auth-switch-to-register">Initialize Protocol</button>
                            </p>
                        ) : (
                             <p className="text-slate-500">
                                Already authenticated? {' '}
                                <button onClick={() => setAuthMode('login')} className="text-emerald-400 font-bold hover:text-emerald-300 transition-colors" data-testid="auth-switch-to-login">Login</button>
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    </div>
  );
};

export default AuthPage;